import { computed, makeObservable, observable } from 'mobx'
import { Model } from 'infra/model'
import Provider from 'iam/models/Provider'
import User from 'iam/models/User'
import { PrimeProvider } from 'iam/ProviderStore'
import { PrimeUser } from 'iam/UserStore'
import { DateZero, maybeParseDate } from 'utils/date-util'
import {
  WorkspaceApplicantDTO,
  WorkspaceApplicantStatusDTO,
} from './dtos/workspace-applicant'

export enum WorkspaceApplicantStatus {
  Applied = 'APPLIED',
  Rejected = 'REJECTED',
  Invited = 'INVITED',
  Accepted = 'ACCEPTED',
  Withdrawn = 'WITHDRAWN',
  Expired = 'EXPIRED',
  Null = 'NULL',
}

export default class WorkspaceApplicant extends Model<
  WorkspaceApplicantDTO,
  WorkspaceApplicant
> {
  user: User = User.none
  provider: Provider = Provider.none
  userPublicId: string = ''
  status: WorkspaceApplicantStatus = WorkspaceApplicantStatus.Applied
  dateCreated: Date = DateZero()
  dateModified: Date = DateZero()
  dateInvited: Date | null = null

  constructor(
    private workspaceId: string,
    private primeUser: PrimeUser,
    private primeProvider: PrimeProvider
  ) {
    super()
    makeObservable(this, {
      user: observable,
      provider: observable,
      userPublicId: observable,
      status: observable,
      dateCreated: observable,
      dateModified: observable,
      dateInvited: observable,
      avatar: computed,
      displayName: computed,
    })
  }

  /**
   * Returns the applicant's avatar, by provider if available, and by user if not.
   */
  get avatar() {
    return this.provider.picture !== ''
      ? this.provider.avatar
      : this.user.avatar
  }

  /**
   * Returns the applicant's display name, by provider if available, and by user if not.
   */
  get displayName() {
    return this.provider.familyName !== ''
      ? this.provider.displayName
      : this.user.displayName
  }

  parse(attrs: WorkspaceApplicantDTO) {
    const user = this.primeUser({
      public_id: attrs.user_public_id,
    })
    const provider = this.primeProvider({
      user_public_id: attrs.user_public_id,
    })

    return {
      user,
      provider,
      userPublicId: attrs.user_public_id,
      status: this.parseWorkspaceApplicantStatusDto(attrs.status),
      dateCreated: maybeParseDate(attrs.date_created),
      dateModified: maybeParseDate(attrs.date_modified),
      dateInvited: maybeParseDate(attrs.date_invited),
    }
  }

  parseWorkspaceApplicantStatusDto(
    dto: WorkspaceApplicantStatusDTO
  ): WorkspaceApplicantStatus {
    switch (dto) {
      case WorkspaceApplicantStatusDTO.Applied:
        return WorkspaceApplicantStatus.Applied
      case WorkspaceApplicantStatusDTO.Rejected:
        return WorkspaceApplicantStatus.Rejected
      case WorkspaceApplicantStatusDTO.Invited:
        return WorkspaceApplicantStatus.Invited
      case WorkspaceApplicantStatusDTO.Accepted:
        return WorkspaceApplicantStatus.Accepted
      case WorkspaceApplicantStatusDTO.Withdrawn:
        return WorkspaceApplicantStatus.Withdrawn
      case WorkspaceApplicantStatusDTO.Expired:
        return WorkspaceApplicantStatus.Expired
      default:
        return WorkspaceApplicantStatus.Null
    }
  }
}
